import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { StudentProfile } from '@/types';

/**
 * Hook to fetch a student's profile by user ID.
 * @param userId The unique identifier of the user (student).
 * @returns An object containing the profile data, loading state, and error state.
 */
export const useStudentProfile = (userId?: string) => {
    const [profile, setProfile] = useState<StudentProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!userId) return;
            setLoading(true);
            try {
                const docRef = doc(db, 'studentProfiles', userId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // Helper to convert dates with safe type checking
                    const toDate = (val: unknown): Date | undefined => {
                        if (!val) return undefined;
                        if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
                            return (val as { toDate: () => Date }).toDate();
                        }
                        if (val instanceof Date) return val;
                        return new Date(val as string | number);
                    };

                    setProfile({
                        ...data,
                        userId: data.userId || userId,
                        joinDate: toDate(data.joinDate),
                        dateOfBirth: toDate(data.dateOfBirth),
                        createdAt: toDate(data.createdAt),
                        updatedAt: toDate(data.updatedAt),
                    } as StudentProfile);
                } else {
                    setProfile(null);
                }
            } catch (err) {
                console.error('Error fetching student profile:', err);
                setError('Failed to fetch profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userId]);

    return { profile, loading, error };
};
